// Implements: F-GEN-033, F-GEN-034, F-UIX-007
/**
 * The two diagram tabs of one version (mockup v3 P6, `DG_TABS`): "Flujo de la pantalla" and
 * "Recorrido entre pantallas". Pure presentation.
 *
 * Each tab carries a dot that is filled when its diagram is drawn, and says so to a screen reader
 * ("(dibujado)" / "(sin dibujar)"), because the dot alone is colour. The tablist follows the ARIA
 * tabs pattern: one tab stop, the arrows and Home/End move and select, the focus follows.
 */

import { useRef } from "react";
import type { KeyboardEvent, ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import type { DiagramKind } from "../../../types/diagrams";
import type { DiagramPaneState } from "../model/diagramState";
import styles from "./DiagramTabs.module.css";

export const DIAGRAM_KINDS: readonly DiagramKind[] = ["journey", "walkthrough"];

const tabId = (idBase: string, kind: DiagramKind): string => `${idBase}-tab-${kind}`;
const panelId = (idBase: string, kind: DiagramKind): string => `${idBase}-panel-${kind}`;

/** The attributes of the panel the selected tab controls. */
export function diagramTabPanelProps(
  idBase: string,
  kind: DiagramKind,
): { id: string; role: "tabpanel"; "aria-labelledby": string; tabIndex: 0 } {
  return { id: panelId(idBase, kind), role: "tabpanel", "aria-labelledby": tabId(idBase, kind), tabIndex: 0 };
}

export interface DiagramTabsProps {
  readonly idBase: string;
  readonly selected: DiagramKind;
  readonly states: Readonly<Record<DiagramKind, DiagramPaneState>>;
  readonly onSelect: (kind: DiagramKind) => void;
}

const LABEL_KEY = { journey: "diagram.tabs.journey", walkthrough: "diagram.tabs.walkthrough" } as const;

export function DiagramTabs({ idBase, selected, states, onSelect }: DiagramTabsProps): ReactElement {
  const { t } = useTranslation();
  const refs = useRef(new Map<DiagramKind, HTMLButtonElement>());

  const move = (event: KeyboardEvent<HTMLDivElement>): void => {
    const current = DIAGRAM_KINDS.indexOf(selected);
    const last = DIAGRAM_KINDS.length - 1;
    const targets: Partial<Record<string, number>> = {
      ArrowRight: current === last ? 0 : current + 1,
      ArrowLeft: current <= 0 ? last : current - 1,
      Home: 0,
      End: last,
    };
    const index = targets[event.key];
    const target = index === undefined ? undefined : DIAGRAM_KINDS[index];
    if (target === undefined) return;
    event.preventDefault();
    onSelect(target);
    refs.current.get(target)?.focus();
  };

  return (
    <div className={styles.tabs} role="tablist" aria-label={t("diagram.tabs.label")} onKeyDown={move} tabIndex={-1}>
      {DIAGRAM_KINDS.map((kind) => {
        const isSelected = kind === selected;
        const drawn = states[kind] === "ready";
        return (
          <button
            key={kind}
            ref={(element) => {
              if (element === null) refs.current.delete(kind);
              else refs.current.set(kind, element);
            }}
            type="button"
            role="tab"
            id={tabId(idBase, kind)}
            className={styles.tab}
            aria-selected={isSelected}
            aria-controls={isSelected ? panelId(idBase, kind) : undefined}
            tabIndex={isSelected ? 0 : -1}
            onClick={() => {
              if (!isSelected) onSelect(kind);
            }}
          >
            <span className={drawn ? styles.dot : styles.dotNone} aria-hidden="true" data-drawn={drawn} />
            <span>{t(LABEL_KEY[kind])}</span>{" "}
            <span className={styles.srOnly}>{t(drawn ? "diagram.tab.drawn" : "diagram.tab.notDrawn")}</span>
          </button>
        );
      })}
    </div>
  );
}
